import {ClientType, getClientType} from '@buncha/common/types/common';
import {
  DeliveryLocationStatus,
  TutorialName,
} from '../../../../batchDetails/types';
import {Dimensions, Platform, StyleSheet} from 'react-native';
import React, {useCallback, useMemo, useState} from 'react';
import {
  useAddNotesForDeliveryLocation,
  useDeliveryDetailsContext,
} from '@buncha/driver/hooks/deliveryDetails';

import {Analytics} from '../../../../config/analytics/analytics';
import {Box} from '../../../../../src/components/core/view';
import {CompleteTaskSuccessParams} from '@buncha/config/onfleet/types';
import {EventType} from '../../../../config/analytics/type';
import {HomeScreens} from '../../../../../src/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {MainScreens} from '../../../../navigation/types';
import {Modules} from '@buncha/utils/types';
import {PButton} from '../../../../../src/components/core/Button';
import {SpinnerComponent} from '../../../../components/core/loader';
import {StorageKeys} from '@buncha/config/storage/type';
import {ToastType} from '../../../../components/composites/notification/type';
import {getWidth} from '../../../../../src/utils/common';
import moment from 'moment';
import {navigation} from '../../../../../src/config/navigation';
import onfleetService from '@buncha/config/onfleet';
import {storage} from '@buncha/config/storage';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useColorThemeType} from '../../../../../src/appStyles/hooks/theme';
import {useFinishTutorialForRunner} from '../../../../home/hooks/Tutorials';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';
import {useSetDeliveryLocationAsFailed} from '@buncha/driver/hooks/orderDetails';
import {useToastMessage} from '../../../../../src/components/composites/notification';
import {verifyOnfleetCompleteParams} from '@buncha/driver/shared/util';

interface ConfirmDeliveryButtonProps {
  deliveryLocationId: number;
  failureReason?: string;
  otherReason?: string;
  bagsUsed?: number;
  orderId?: string;
  taskId?: string;
  isB2BStore: boolean;
}

const ConfirmDeliveryButton: React.FC<ConfirmDeliveryButtonProps> = props => {
  const theme = useColorThemeType('main');
  const routeParams: any = useRoute().params;
  const tutorial = routeParams?.tutorial === 'true';
  const [showSuccessToast] = useToastMessage(ToastType.Success);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const {state} = useDeliveryDetailsContext();
  const {button} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const {finishTutorialForRunner, tutorialLoading} =
    useFinishTutorialForRunner();

  const {addNotesForDeliveryLocation} = useAddNotesForDeliveryLocation();
  const {setDeliveryLocationStatusAsFailed, loading: failedLoading} =
    useSetDeliveryLocationAsFailed();

  const [loading, setLoading] = useState(false);
  const deliveryLocationId = props?.deliveryLocationId || 0;
  const failureReason = props?.failureReason;

  const currentTaskId = props?.taskId;

  const currentTask = useAppSelector(gState => gState.home.driverTasks)?.find(
    task => task.id === currentTaskId,
  );

  const addNote = useCallback(() => {
    if (
      deliveryLocationId &&
      state.deliveryNote &&
      currentTask?.metadata?.runId
    )
      addNotesForDeliveryLocation(
        deliveryLocationId,
        state.deliveryNote,
        currentTask?.metadata?.runId,
      );
  }, [
    addNotesForDeliveryLocation,
    currentTask?.metadata?.runId,
    deliveryLocationId,
    state.deliveryNote,
  ]);

  const isDeliverySuccessDisabled = useMemo(() => {
    const taskCompletionDetails: CompleteTaskSuccessParams = {
      success: true,
      ageDetails: state.idCaptureDetails,
      barcodes: state.barcodes,
      photos: state.deliveryImages,
      notes: state.deliveryNote,
      signatureDetails: {
        name: state.signatureImage,
      },
    };
    const inValidMessage =
      currentTask &&
      verifyOnfleetCompleteParams(currentTask, taskCompletionDetails);
    return Boolean(inValidMessage?.length);
  }, [
    currentTask,
    state.barcodes,
    state.deliveryImages,
    state.deliveryNote,
    state.idCaptureDetails,
    state.signatureImage,
  ]);

  const handleTimestampUpdate = useCallback(async () => {
    let deliveryTimestamps: any = {};
    try {
      const deliveryTimestampsString = await storage.getItem(
        StorageKeys.deliveryTimestamps,
      );
      deliveryTimestamps = JSON.parse(deliveryTimestampsString ?? '{}');
    } catch {
      deliveryTimestamps = {};
    }

    if (props.orderId) {
      deliveryTimestamps[props.orderId] = moment().format();
      await storage.setItem(
        StorageKeys.deliveryTimestamps,
        JSON.stringify(deliveryTimestamps),
      );
    }
  }, [props.orderId]);

  const markOrderAsDelivered = useCallback(async () => {
    if (tutorial) {
      await finishTutorialForRunner(TutorialName.driverGuide);
      return navigation.navigate(MainScreens.Home, {
        screen: HomeScreens.Tutorials,
      });
    }
    const taskCompletionDetails: CompleteTaskSuccessParams = {
      success: true,
      ageDetails: state.idCaptureDetails,
      barcodes: state.barcodes,
      photos: state.deliveryImages,
      notes: state.deliveryNote,
      signatureDetails: {
        name: state.signatureImage,
      },
    };
    const inValidMessage =
      currentTask &&
      verifyOnfleetCompleteParams(currentTask, taskCompletionDetails);
    if (inValidMessage) return showErrorToast(inValidMessage);
    if (!currentTask?.id) return;
    setLoading(true);
    const res = await onfleetService.completeTask(
      currentTask?.id,
      taskCompletionDetails,
    );
    setLoading(false);
    if (!res) return;
    showSuccessToast('Order has been delivered');
    Analytics.eventWithProps('Confirm delivery', {
      type: EventType.cta,
      previousStatus: DeliveryLocationStatus.onTheWay,
      deliveryLocationId: deliveryLocationId,
      status: DeliveryLocationStatus.delivered,
      countOfBarcodes: currentTask?.barcodes?.length,
      photoAdded: Boolean(state.deliveryImages?.length),
      signatureAdded: Boolean(state.signatureImage),
      barcodeAdded: currentTask?.barcodes,
      idScanned: state.idCaptureDetails ? true : false,
      id: currentTask?.id,
      bagsUsed: props?.bagsUsed,
      ...(getClientType(props?.isB2BStore) === ClientType.meijer && {
        legacyId: props?.orderId,
      }),
      ...(getClientType(props?.isB2BStore) === ClientType.buncha && {
        orderId: props?.orderId,
      }),
      userType: 'driver',
      client: getClientType(props?.isB2BStore),
    });
    addNote();
    handleTimestampUpdate();
    navigation.pop(2);
  }, [
    handleTimestampUpdate,
    addNote,
    currentTask,
    deliveryLocationId,
    finishTutorialForRunner,
    props?.bagsUsed,
    props?.isB2BStore,
    props?.orderId,
    showErrorToast,
    showSuccessToast,
    state.barcodes,
    state.deliveryImages,
    state.deliveryNote,
    state.idCaptureDetails,
    state.signatureImage,
    tutorial,
  ]);

  const markOrderAsFailed = useCallback(async () => {
    setLoading(true);
    if (currentTask?.id) {
      if (Number(currentTask?.metadata?.runId))
        await setDeliveryLocationStatusAsFailed(
          deliveryLocationId,
          DeliveryLocationStatus.deliveryFailed,
          props.otherReason || failureReason || '',
          Number(currentTask?.metadata?.runId),
        );
      await onfleetService.completeTask(currentTask.id, {
        success: false,
        failureReason: props.otherReason || failureReason,
        notes: state.deliveryNote,
      });
    }
    Analytics.eventWithProps('Mark delivery as failed', {
      type: 'CTA',
      deliveryLocationId: deliveryLocationId,
      reason: failureReason,
      otherReason: props?.otherReason,
      bagsUsed: props?.bagsUsed,
      runId: currentTask?.metadata?.runId,
      deliveryStatus: DeliveryLocationStatus.deliveryFailed,
      userType: 'driver',
      countOfBarcodes: currentTask?.barcodes?.length,
      photoAdded: Boolean(state.deliveryImages?.length),
      signatureAdded: Boolean(state.signatureImage),
      barcodeAdded: currentTask?.barcodes,
      id: currentTask?.id,
      client: getClientType(props?.isB2BStore),
    });
    setLoading(false);
    addNote();
    navigation.pop(2);
  }, [
    failureReason,
    state.deliveryNote,
    state.deliveryImages?.length,
    state.signatureImage,
    deliveryLocationId,
    props?.otherReason,
    props?.bagsUsed,
    props?.isB2BStore,
    currentTask,
    addNote,
    setDeliveryLocationStatusAsFailed,
  ]);

  return (
    <Box style={[styles.parentBox, {borderColor: theme?.text?.gray?.[200]}]}>
      <If condition={!failureReason}>
        <PButton
          style={styles.bottomCTA}
          title={`${button?.completeDelivery?.[0]}${
            tutorial ? button?.completeDelivery?.[1] : ''
          }`}
          isLoading={loading || tutorialLoading}
          onPress={markOrderAsDelivered}
          disabled={loading || tutorialLoading || isDeliverySuccessDisabled}
          loadingIndicator={<SpinnerComponent />}
        />
      </If>
      <If condition={failureReason}>
        <PButton
          style={styles.bottomCTA}
          bgColor={theme?.icon.red[100]}
          title={button?.failure}
          isLoading={loading || tutorialLoading || failedLoading}
          onPress={markOrderAsFailed}
          disabled={loading || tutorialLoading || failedLoading}
          loadingIndicator={<SpinnerComponent />}
        />
      </If>
    </Box>
  );
};

export default React.memo(ConfirmDeliveryButton);

const styles = StyleSheet.create({
  parentBox: {
    paddingTop: 15,
    borderTopWidth: 1,
    bottom: 0,
    width: getWidth(Dimensions?.get('window')?.width),
  },
  bottomCTA: {
    width: '90%',
    alignSelf: 'center',
    borderRadius: 50,
    marginBottom: Platform.select({
      android: 15,
      ios: 0,
    }),
  },
});
